import { ApolloError } from '@apollo/client';
import { isEqual, uniq } from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { BdgResolverEntityInput } from '@/modules/entityDiagram/bdgTriggerInfo';
import { collectDownstreamRecipients } from '@/modules/entityDiagram/collectDownstreamRecipients';
import { RecipientDispositions } from '@/modules/entityDiagram/getDispositionsVizFromEntity';
import {
  useDispositionsDiagramClientProfilesLazyQuery,
  useDispositionsDiagramLazyQuery,
  useDispositionsDiagramTestamentaryEntitiesLazyQuery,
} from '@/modules/entityDiagram/graphql/DispositionsDiagram.generated';
import { resolveEntityNormalizedDispositions } from '@/modules/entityDiagram/resolveEntityNormalizedDispositions';
import { useWaterfallSummaryContext } from '@/modules/estateWaterfall/contexts/waterfallSummary.context';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import {
  AfterDeath,
  DispositiveProvisionRecipientKind,
  DispositiveProvisionTransferTaxKind,
} from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';

import { useDispositiveDiagramSyncContext } from '../contexts/DispositiveDiagramSync.context';
import {
  DispositiveProvisionsFormRecipient,
  RecipientKind,
} from '../DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { SURVIVING_SPOUSE_SENTINEL } from '../DispositiveProvisionsForm/hooks/useRecipientOptions';

/**
 * Prefetch dispositions for recipients that can distribute before the final
 * diagram section, or continue a pour-over chain in the final section.
 */
export function usePrefetchRecipientDispositions({
  firstDeathRecipients,
  secondDeathRecipients,
  firstGrantorDeathId,
  survivingSpouseRecipientId,
}: {
  firstDeathRecipients: DispositiveProvisionsFormRecipient[] | undefined | null;
  secondDeathRecipients?: DispositiveProvisionsFormRecipient[] | null;
  firstGrantorDeathId: string | null;
  survivingSpouseRecipientId: string | null;
}) {
  const {
    recipientDispositions,
    setRecipientDispositions,
    setRecipientDispositionWarning,
    setIsLoadingRecipientDispositions,
  } = useDispositiveDiagramSyncContext();

  const { primaryClients } = useHouseholdDetailsContext();
  const bdgPossiblePrimaryClients = useMemo(
    () => (primaryClients ?? []).map((c) => ({ id: c.id })),
    [primaryClients]
  );
  // Hypothetical-scope BDGs / scenarios mirror what the parent diagram is
  // rendering. Null in the live context (server falls back to live values).
  const { waterfall } = useWaterfallSummaryContext();
  const hypotheticalWaterfallId = waterfall?.id ?? null;

  // Track the current request to handle cleanup
  const currentRequestRef = useRef<{ aborted: boolean }>({ aborted: false });

  const onError = useCallback((error: ApolloError) => {
    diagnostics.error('prefetch recipient dispositions failed', error);
  }, []);

  const options = {
    fetchPolicy: 'no-cache' as const,
    onError,
  };

  const [getDispositions] = useDispositionsDiagramLazyQuery(options);

  const [getDispositionsForTestamentaryEntities] =
    useDispositionsDiagramTestamentaryEntitiesLazyQuery(options);

  const [getDispositionsForClientProfiles] =
    useDispositionsDiagramClientProfilesLazyQuery(options);

  const process = useCallback(
    async (
      entityIds: string[],
      testamentaryEntityIds: string[],
      clientProfileIds: string[],
      pourOverTargetIds: Set<string>,
      requestTracker: { aborted: boolean },
      loadedDispositions: RecipientDispositions
    ) => {
      const hasRecipients =
        entityIds.length > 0 ||
        testamentaryEntityIds.length > 0 ||
        clientProfileIds.length > 0;

      if (!hasRecipients) {
        setIsLoadingRecipientDispositions(false);
        return;
      }

      // Set loading state immediately before starting async operations
      setIsLoadingRecipientDispositions(true);
      const pendingDispositions: RecipientDispositions = {};
      const directRecipientIds = new Set([
        ...entityIds,
        ...testamentaryEntityIds,
        ...clientProfileIds,
      ]);
      const storeDispositions = (
        id: string,
        dispositions: RecipientDispositions[string]
      ) => {
        pendingDispositions[id] = dispositions;
      };

      try {
        // Type does not matter
        const promises: Promise<unknown>[] = [];

        // Process entity dispositions
        if (entityIds.length > 0 && !requestTracker.aborted) {
          promises.push(
            getDispositions({
              variables: {
                entityWhere: {
                  idIn: entityIds,
                },
                hypotheticalWaterfallId,
              },
            }).then((result) => {
              if (requestTracker.aborted || !result.data) return;
              const entities = getNodes(result.data.entities);
              for (const rawEntity of entities) {
                if (!rawEntity.id || !firstGrantorDeathId) continue;
                const resolved = resolveEntityNormalizedDispositions(
                  rawEntity,
                  {
                    firstGrantorDeathId,
                    possiblePrimaryClients: bdgPossiblePrimaryClients,
                    placedAt: AfterDeath.First,
                  }
                );
                if (requestTracker.aborted) return;
                storeDispositions(rawEntity.id, {
                  firstDeath: resolved?.firstDeath ?? [],
                  secondDeath: resolved?.secondDeath ?? [],
                  missingBeneficiaryTier: resolved?.missingBeneficiaryTier,
                });

                // The fetched recipient can pour over into another entity even
                // when the root makes a bequest to it. Read the inline scenarios
                // for warning evidence without adding downstream chart branches.
                collectDownstreamRecipients(rawEntity, {
                  includePrimaryClientScenarios: false,
                }).forEach((downstreamEntity, downstreamId) => {
                  if (requestTracker.aborted) return;
                  const downstreamResolved =
                    resolveEntityNormalizedDispositions(downstreamEntity, {
                      firstGrantorDeathId,
                      possiblePrimaryClients: bdgPossiblePrimaryClients,
                      placedAt: AfterDeath.First,
                    });
                  setRecipientDispositionWarning(
                    downstreamId,
                    (downstreamResolved?.firstDeath.length ?? 0) > 0 ||
                      (downstreamResolved?.secondDeath.length ?? 0) > 0
                  );

                  // Keep full disposition loading limited to the existing
                  // direct pour-over path. Warning evidence is stored separately
                  // so it cannot replace loaded query or form dispositions.
                  if (
                    !pourOverTargetIds.has(rawEntity.id) ||
                    !downstreamResolved ||
                    directRecipientIds.has(downstreamId)
                  )
                    return;
                  storeDispositions(downstreamId, {
                    firstDeath: downstreamResolved.firstDeath,
                    secondDeath: downstreamResolved.secondDeath,
                    missingBeneficiaryTier:
                      downstreamResolved.missingBeneficiaryTier,
                  });
                });
              }
            })
          );
        }

        // Process testamentary entity dispositions
        if (testamentaryEntityIds.length > 0 && !requestTracker.aborted) {
          promises.push(
            getDispositionsForTestamentaryEntities({
              variables: {
                testamentWhere: {
                  idIn: testamentaryEntityIds,
                },
                hypotheticalWaterfallId,
              },
            }).then((result) => {
              // Check if request was aborted before processing results
              if (requestTracker.aborted) return;

              if (result.data) {
                const testamentaryEntities = getNodes(
                  result.data.testamentaryEntities
                );
                for (const testamentaryEntity of testamentaryEntities) {
                  const testamentaryEntityId = testamentaryEntity.id;
                  if (!testamentaryEntityId) continue;

                  const dispositionScenario =
                    testamentaryEntity.dispositionScenarios?.find(
                      (scenario) =>
                        scenario.firstGrantorDeath?.id === firstGrantorDeathId
                    );

                  const firstDeathDispositions = getNodes(
                    dispositionScenario?.dispositiveProvisionsTemplate
                      ?.dispositiveProvisions ??
                      dispositionScenario?.dispositiveProvisions
                  );

                  const secondDeathDispositions = getNodes(
                    dispositionScenario
                      ?.secondDeathDispositiveProvisionsTemplate
                      ?.dispositiveProvisions ??
                      dispositionScenario?.secondDeathDispositiveProvisions
                  );

                  if (!requestTracker.aborted) {
                    storeDispositions(testamentaryEntityId, {
                      firstDeath: firstDeathDispositions ?? [],
                      secondDeath: secondDeathDispositions ?? [],
                    });
                  }
                }
              }
            })
          );
        }

        // Process client profile dispositions
        if (clientProfileIds.length > 0 && !requestTracker.aborted) {
          promises.push(
            getDispositionsForClientProfiles({
              variables: {
                clientProfileWhere: {
                  idIn: clientProfileIds,
                },
                hypotheticalWaterfallId,
              },
            }).then((result) => {
              // Check if request was aborted before processing results
              if (requestTracker.aborted) return;

              if (result.data) {
                const clientProfiles = getNodes(result.data.clientProfiles);
                for (const clientProfile of clientProfiles) {
                  const clientProfileId = clientProfile.id;
                  if (!clientProfileId) continue;

                  const dispositionScenario =
                    clientProfile.dispositionScenarios?.find(
                      (scenario) =>
                        scenario.firstGrantorDeath?.id === firstGrantorDeathId
                    );

                  const firstDeathDispositions = getNodes(
                    dispositionScenario?.dispositiveProvisionsTemplate
                      ?.dispositiveProvisions ??
                      dispositionScenario?.dispositiveProvisions
                  );

                  const secondDeathDispositions = getNodes(
                    dispositionScenario
                      ?.secondDeathDispositiveProvisionsTemplate
                      ?.dispositiveProvisions ??
                      dispositionScenario?.secondDeathDispositiveProvisions
                  );

                  if (!requestTracker.aborted) {
                    storeDispositions(clientProfileId, {
                      firstDeath: firstDeathDispositions ?? [],
                      secondDeath: secondDeathDispositions ?? [],
                    });

                    if (!firstGrantorDeathId) continue;
                    [
                      ...firstDeathDispositions,
                      ...secondDeathDispositions,
                    ].forEach((disposition) => {
                      const recipient = (() => {
                        if (disposition.entity?.subtype) {
                          return disposition.entity;
                        }
                        const scenarioCarrier =
                          disposition.testamentaryEntity ??
                          disposition.individual;
                        if (!scenarioCarrier?.dispositionScenarios) return null;
                        return {
                          id: scenarioCarrier.id,
                          subtype: {
                            dispositionScenarios:
                              scenarioCarrier.dispositionScenarios,
                          },
                        };
                      })();
                      if (!recipient?.id) return;
                      // Nested recipients use terminal fragments, which
                      // include all fields that the resolver reads.
                      const resolved = resolveEntityNormalizedDispositions(
                        recipient as unknown as BdgResolverEntityInput,
                        {
                          firstGrantorDeathId,
                          possiblePrimaryClients: bdgPossiblePrimaryClients,
                          placedAt: AfterDeath.First,
                        }
                      );
                      setRecipientDispositionWarning(
                        recipient.id,
                        (resolved?.firstDeath.length ?? 0) > 0 ||
                          (resolved?.secondDeath.length ?? 0) > 0
                      );
                    });
                  }
                }
              }
            })
          );
        }

        // Wait for all queries to complete
        await Promise.allSettled(promises);
      } catch (error) {
        diagnostics.error(
          'prefetch recipient dispositions failed',
          error as Error,
          {
            entityIds: entityIds.join(', '),
            testamentaryEntityIds: testamentaryEntityIds.join(', '),
            clientProfileIds: clientProfileIds.join(', '),
          }
        );
      } finally {
        if (!requestTracker.aborted) {
          // Commit each recipient once. Inline fragments must not replace a
          // direct response or cause another round when the final values match.
          let hasUpdatedDispositions = false;
          Object.entries(pendingDispositions).forEach(([id, dispositions]) => {
            if (isEqual(loadedDispositions[id], dispositions)) return;
            hasUpdatedDispositions = true;
            setRecipientDispositions(id, dispositions);
          });
          // Keep loading active until the traversal of changed data finishes.
          if (!hasUpdatedDispositions) {
            setIsLoadingRecipientDispositions(false);
          }
        }
      }
    },
    [
      bdgPossiblePrimaryClients,
      firstGrantorDeathId,
      getDispositions,
      getDispositionsForClientProfiles,
      getDispositionsForTestamentaryEntities,
      hypotheticalWaterfallId,
      setIsLoadingRecipientDispositions,
      setRecipientDispositions,
      setRecipientDispositionWarning,
    ]
  );

  useEffect(() => {
    // Abort any previous request
    currentRequestRef.current.aborted = true;

    // Create a new request tracker scoped to this effect
    const requestTracker = { aborted: false };
    currentRequestRef.current = requestTracker;

    // Calculate recipients at the time of effect execution
    // to avoid stale closure issues
    const processRecipients = () => {
      const recipients = [
        ...(firstDeathRecipients ?? []),
        ...(secondDeathRecipients ?? []),
      ];
      const entityRecipients = recipients.filter(
        (r) => r.recipientKind === RecipientKind.Entity
      );
      const testamentaryEntityRecipients = recipients.filter(
        (r) => r.recipientKind === RecipientKind.TestamentaryEntity
      );
      const individualRecipients = recipients.filter(
        (r) =>
          r.recipientKind === RecipientKind.Individual ||
          r.recipientKind === RecipientKind.SurvivingSpouse
      );

      // Get current entity IDs at the time of processing
      const currentEntityIds = Object.keys(recipientDispositions);

      const filterRecipients = (
        recipients: DispositiveProvisionsFormRecipient[]
      ): string[] => {
        return uniq(
          recipients
            .flatMap((r) => r.recipientId)
            .filter((id): id is string => !!id)
            .map((id) => {
              // Replace SURVIVING_SPOUSE_SENTINEL with actual client profile ID
              if (
                id === SURVIVING_SPOUSE_SENTINEL &&
                survivingSpouseRecipientId
              ) {
                return survivingSpouseRecipientId;
              }
              return id;
            })
            .filter((id) => id !== SURVIVING_SPOUSE_SENTINEL) // Filter out any remaining sentinel values without corresponding ID
            .flatMap((id) => {
              if (currentEntityIds.includes(id)) return [];
              return id;
            })
        );
      };

      const entityIds = filterRecipients(entityRecipients);
      const testamentaryEntityIds = filterRecipients(
        testamentaryEntityRecipients
      );
      const clientProfileIds = filterRecipients(individualRecipients);

      // Follow loaded recipients in the same sections as the renderer. A
      // pour-over does not advance the death section, so its recipient can
      // still distribute even after an ordinary first-death bequest.
      const visited = new Set<string>();
      const followRecipient = (
        id: string | null | undefined,
        kind: RecipientKind | null,
        section: AfterDeath,
        receivedViaPourover = false,
        isSecondDeathContinuation = false
      ): void => {
        if (id === SURVIVING_SPOUSE_SENTINEL) id = survivingSpouseRecipientId;
        if (!id) return;
        const loaded = recipientDispositions[id];
        if (
          section === AfterDeath.Second &&
          loaded &&
          !receivedViaPourover &&
          !isSecondDeathContinuation &&
          !loaded.secondDeath.some(
            (provision) =>
              provision.transferTaxKind ===
              DispositiveProvisionTransferTaxKind.Pourover
          )
        )
          return;
        const usesFirstDeath =
          !isSecondDeathContinuation &&
          (section === AfterDeath.None ||
            (receivedViaPourover && (loaded?.firstDeath.length ?? 0) > 0));
        const visitKey = `${id}:${section}:${usesFirstDeath ? 'firstDeath' : 'secondDeath'}`;
        if (visited.has(visitKey)) return;
        visited.add(visitKey);

        if (!loaded) {
          if (kind === RecipientKind.Entity) entityIds.push(id);
          if (kind === RecipientKind.TestamentaryEntity) {
            testamentaryEntityIds.push(id);
          }
          if (
            kind === RecipientKind.Individual ||
            kind === RecipientKind.SurvivingSpouse
          )
            clientProfileIds.push(id);
          return;
        }

        const provisions = usesFirstDeath
          ? loaded.firstDeath
          : loaded.secondDeath;
        provisions.forEach((provision) => {
          const isPourover =
            provision.transferTaxKind ===
            DispositiveProvisionTransferTaxKind.Pourover;
          let recipientSection: AfterDeath = section;
          if (!isPourover) {
            recipientSection =
              section === AfterDeath.None
                ? AfterDeath.First
                : AfterDeath.Second;
          }
          if (provision.entity) {
            followRecipient(
              provision.entity.id,
              RecipientKind.Entity,
              recipientSection,
              isPourover
            );
          } else if (provision.testamentaryEntity) {
            followRecipient(
              provision.testamentaryEntity.id,
              RecipientKind.TestamentaryEntity,
              recipientSection,
              isPourover
            );
          } else if (provision.individual) {
            followRecipient(
              provision.individual.id,
              RecipientKind.Individual,
              recipientSection,
              isPourover
            );
          } else if (
            provision.recipientKind ===
            DispositiveProvisionRecipientKind.SurvivingSpouse
          ) {
            followRecipient(
              survivingSpouseRecipientId,
              RecipientKind.SurvivingSpouse,
              recipientSection,
              isPourover
            );
          }
        });
        // Continue in the final section when no further boundary exists.
        if (usesFirstDeath && loaded.secondDeath.length > 0) {
          const nextSection =
            section === AfterDeath.None ? AfterDeath.First : AfterDeath.Second;
          followRecipient(id, kind, nextSection, false, true);
        }
      };
      firstDeathRecipients?.forEach((recipient) => {
        const section =
          recipient.transferTaxKind ===
          DispositiveProvisionTransferTaxKind.Pourover
            ? AfterDeath.None
            : AfterDeath.First;
        followRecipient(
          recipient.recipientId,
          recipient.recipientKind,
          section
        );
      });
      secondDeathRecipients?.forEach((recipient) => {
        const isPourover =
          recipient.transferTaxKind ===
          DispositiveProvisionTransferTaxKind.Pourover;
        followRecipient(
          recipient.recipientId,
          recipient.recipientKind,
          isPourover ? AfterDeath.First : AfterDeath.Second,
          isPourover
        );
      });

      // Entity recipients the edited entity pours over into. Their own
      // recipients sit one tier deeper than a normal hop, so the fetch below
      // looks one level past these targets to populate depth-3 dispositions.
      const pourOverTargetIds = new Set(
        entityRecipients
          .filter(
            (r) =>
              r.transferTaxKind === DispositiveProvisionTransferTaxKind.Pourover
          )
          .flatMap((r) => (r.recipientId ? [r.recipientId] : []))
      );

      // Always fetch pour-over targets, even if filterRecipients already deduped
      // them (their depth-2 dispositions are cached). Depth-3 is resolved from a
      // target's fetch result, so a target cached before its transferTaxKind
      // resolved to pour-over would otherwise never get its look-through. The
      // setter is idempotent (DispositiveDiagramSync), so the extra fetch can't
      // loop.
      const entityIdsToFetch = uniq([
        ...entityIds,
        ...Array.from(pourOverTargetIds),
      ]);

      void process(
        entityIdsToFetch,
        uniq(testamentaryEntityIds),
        uniq(clientProfileIds),
        pourOverTargetIds,
        requestTracker,
        recipientDispositions
      );
    };

    processRecipients();

    // Cleanup function to abort the request if the component unmounts
    // or dependencies change
    return () => {
      requestTracker.aborted = true;
    };
  }, [
    firstDeathRecipients,
    process,
    recipientDispositions,
    secondDeathRecipients,
    survivingSpouseRecipientId,
  ]);
}
